import React, { Suspense, useRef, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, useGLTF } from '@react-three/drei';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader';
import styled from 'styled-components';
import * as THREE from 'three';
import { useLipSync } from '../hooks/useLipSync';

interface Avatar3DProps {
  modelPath?: string;
  emotion?: 'idle' | 'listening' | 'thinking' | 'speaking' | 'dancing';
  isAnimating?: boolean;
  shouldGreet?: boolean;
  isDancing?: boolean;
  audioElement?: HTMLAudioElement | null;
}

export interface Avatar3DRef {
  startLipSync: (audioElement: HTMLAudioElement) => void;
  stopLipSync: () => void;
  resetMouth: () => void;
}

const CanvasContainer = styled.div`
  width: 100%;
  height: 100%;
  min-height: 400px;
`;

const LoadingText = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #666;
  font-size: 18px;
`;

function AvatarModel({ 
  modelPath, 
  emotion = 'idle', 
  isAnimating = false, 
  shouldGreet = false,
  isDancing = false,
  audioElement = null,
  onModelLoaded
}: { 
  modelPath: string; 
  emotion: string;
  isAnimating: boolean;
  shouldGreet: boolean;
  isDancing: boolean;
  audioElement: HTMLAudioElement | null;
  onModelLoaded: (model: THREE.Group) => void;
}) {
  const meshRef = useRef<THREE.Group>(null);
  const [model, setModel] = useState<THREE.Group | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [mixer, setMixer] = useState<THREE.AnimationMixer | null>(null);
  const [animations, setAnimations] = useState<{ [key: string]: THREE.AnimationAction }>({});
  const [currentAnimation, setCurrentAnimation] = useState<string>('idle');
  const [hasGreeted, setHasGreeted] = useState(false);

  // Estados para parpadeo
  const [isBlinking, setIsBlinking] = useState<boolean>(false);
  const [lastBlinkTime, setLastBlinkTime] = useState<number>(0);

  // Hook de lipsync
  const { analyzeAudio, stopLipSync, resetMouth } = useLipSync();

  // Efecto para manejar lipsync cuando hay audio
  useEffect(() => {
    if (audioElement && model && emotion === 'speaking') {
      console.log('🎤 Iniciando lipsync...');
      analyzeAudio(audioElement, model);
      
      // Listener para cuando termine el audio
      const handleAudioEnd = () => {
        console.log('🔇 Audio terminado - cerrando boca...');
        resetMouth(model);
      };
      
      audioElement.addEventListener('ended', handleAudioEnd);
      
      return () => {
        console.log('🔇 Deteniendo lipsync...');
        stopLipSync(model);
        audioElement.removeEventListener('ended', handleAudioEnd);
      };
    } else if (!audioElement || emotion !== 'speaking') {
      if (model) {
        stopLipSync(model);
      }
    }
  }, [audioElement, model, emotion, analyzeAudio, stopLipSync, resetMouth]);
  const handleBlinking = (currentTime: number) => {
    // Parpadear cada 3-6 segundos aleatoriamente
    const timeSinceLastBlink = currentTime - lastBlinkTime;
    const nextBlinkTime = 3000 + Math.random() * 3000; // 3-6 segundos

    if (timeSinceLastBlink > nextBlinkTime) {
      setIsBlinking(true);
      setLastBlinkTime(currentTime);

      // Duración del parpadeo: 150-300ms
      const blinkDuration = 150 + Math.random() * 150;

      setTimeout(() => {
        setIsBlinking(false);
      }, blinkDuration);
    }
  };

  // Función para aplicar parpadeo
  const applyBlinking = () => {
    if (!model) return;

    model.traverse((child: THREE.Object3D) => {
      if (
        child instanceof THREE.SkinnedMesh &&
        child.morphTargetDictionary &&
        child.morphTargetInfluences
      ) {
        const eyeTargets = ['eyeBlinkLeft', 'eyeBlinkRight', 'eyesClose'];

        eyeTargets.forEach((targetName) => {
          const index = child.morphTargetDictionary![targetName];
          if (index !== undefined && child.morphTargetInfluences) {
            const targetValue = isBlinking ? 1.0 : 0.0;
            child.morphTargetInfluences[index] = THREE.MathUtils.lerp(
              child.morphTargetInfluences[index] || 0,
              targetValue,
              0.3 // Parpadeo rápido
            );
          }
        });
      }
    });
  };

  // Cargar modelo y animaciones
  useEffect(() => {
    const loadModelAndAnimations = async () => {
      try {
        // Cargar modelo GLB
        const gltfLoader = new GLTFLoader();
        console.log('🎭 Cargando Greta.glb...');

        const gltf = await new Promise<any>((resolve, reject) => {
          gltfLoader.load(
            modelPath,
            resolve,
            (progress) => {
              const percentage = progress.total > 0 ? Math.round((progress.loaded / progress.total) * 100) : 0;
              console.log('📥 Progreso modelo:', percentage + '%');
            },
            reject
          );
        });

        console.log('✅ Greta.glb cargado exitosamente');
        const loadedModel = gltf.scene;

        // Configurar modelo
        loadedModel.traverse((child: THREE.Object3D) => {
          if (child instanceof THREE.Mesh) {
            child.castShadow = true;
            child.receiveShadow = true;
            if (child.material) {
              if (Array.isArray(child.material)) {
                child.material.forEach((mat) => {
                  if (mat instanceof THREE.MeshStandardMaterial) {
                    mat.envMapIntensity = 0.5;
                  }
                });
              } else if (child.material instanceof THREE.MeshStandardMaterial) {
                child.material.envMapIntensity = 0.5;
              }
            }
          }
        });

        // Crear mixer de animaciones
        const animationMixer = new THREE.AnimationMixer(loadedModel);
        setMixer(animationMixer);

        // Cargar animaciones FBX
        const fbxLoader = new FBXLoader();
        const animationFiles = [
          { name: 'idle', file: 'Standing Idle.fbx' },
          { name: 'idle2', file: 'Standing Idle2.fbx' },
          { name: 'idle3', file: 'Standing Idle3.fbx' },
          { name: 'idle4', file: 'Standing Idle4.fbx' },
          { name: 'idle5', file: 'Standing Idle5.fbx' },
          { name: 'talking0', file: 'Talking_0.fbx' },
          { name: 'talking1', file: 'Talking_1.fbx' },
          { name: 'talking2', file: 'Talking_2.fbx' },
          { name: 'talking3', file: 'Talking_3.fbx' },
          { name: 'saludo', file: 'Saludo.fbx' },
          { name: 'dancing', file: 'Rumba Dancing.fbx' },
          { name: 'thinking', file: 'Thinking.fbx' },
          { name: 'walking', file: 'Walking.fbx' },
        ];

        const loadedAnimations: { [key: string]: THREE.AnimationAction } = {};

        for (const animFile of animationFiles) {
          try {
            console.log(`🎬 Cargando animación: ${animFile.name}...`);

            const fbx = await new Promise<any>((resolve, reject) => {
              fbxLoader.load(
                `/animations/${animFile.file}`,
                resolve,
                (progress) => {
                  const percentage = progress.total > 0 ? Math.round((progress.loaded / progress.total) * 100) : 0;
                  console.log(`📥 Progreso ${animFile.name}:`, percentage + '%');
                },
                reject
              );
            });

            if (fbx.animations && fbx.animations.length > 0) {
              // Remover root motion para evitar movimiento de posición
              fbx.animations[0].tracks = fbx.animations[0].tracks.filter(
                (track: THREE.KeyframeTrack) => !track.name.includes('.position')
              );

              const action = animationMixer.clipAction(fbx.animations[0]);
              action.setLoop(THREE.LoopRepeat, Infinity);
              loadedAnimations[animFile.name] = action;
              console.log(`✅ Animación ${animFile.name} cargada`);
            }
          } catch (error) {
            console.warn(`⚠️ No se pudo cargar animación ${animFile.name}:`, error);
          }
        }

        setAnimations(loadedAnimations);
        setModel(loadedModel);
        onModelLoaded(loadedModel);
        setIsLoading(false);

        // Iniciar animación idle por defecto
        if (loadedAnimations.idle) {
          loadedAnimations.idle.play();
          setCurrentAnimation('idle');
          console.log('🎭 Animación idle iniciada');
        }
      } catch (error) {
        console.error('❌ Error cargando modelo:', error);
        setIsLoading(false);
      }
    };

    loadModelAndAnimations();
  }, [modelPath]);

  // Función para cambiar animación
  const changeAnimation = (newAnimation: string) => {
    if (!mixer || !animations[newAnimation] || currentAnimation === newAnimation) return;

    console.log(`🎭 Cambiando animación: ${currentAnimation} → ${newAnimation}`);

    // Para saludo, usar transición especial
    if (newAnimation === 'saludo' || currentAnimation === 'saludo') {
      if (animations[currentAnimation]) {
        animations[currentAnimation].stop();
      }

      if (newAnimation === 'saludo') {
        animations[newAnimation].reset();
        animations[newAnimation].setLoop(THREE.LoopOnce, 1);
        animations[newAnimation].clampWhenFinished = true;
        animations[newAnimation].play();
      } else {
        animations[newAnimation].reset().play();
      }
    } else {
      // Transición suave para otras animaciones
      if (animations[currentAnimation]) {
        animations[currentAnimation].fadeOut(0.3);
      }
      animations[newAnimation].reset().fadeIn(0.3).play();
    }

    setCurrentAnimation(newAnimation);
  };

  // Cambiar animación según emoción
  useEffect(() => {
    if (!mixer || Object.keys(animations).length === 0) return;

    let targetAnimation = 'idle';

    switch (emotion) {
      case 'speaking':
        const availableTalking = ['talking0', 'talking1', 'talking2', 'talking3'].filter(name => animations[name]);
        targetAnimation = availableTalking[Math.floor(Math.random() * availableTalking.length)] || 'idle';
        break;
      case 'thinking':
        targetAnimation = animations.thinking ? 'thinking' : 'idle';
        break;
      case 'listening':
        targetAnimation = 'idle';
        break;
      case 'dancing':
        targetAnimation = animations.dancing ? 'dancing' : 'idle';
        break;
      default:
        targetAnimation = 'idle';
    }

    changeAnimation(targetAnimation);
  }, [emotion, mixer, animations]);

  // Manejar saludo inicial
  useEffect(() => {
    if (shouldGreet && !hasGreeted && animations.saludo) {
      console.log('👋 Iniciando animación de saludo...');
      changeAnimation('saludo');
      setHasGreeted(true);

      // Volver a idle después del saludo
      setTimeout(() => {
        console.log('🔄 Volviendo a idle después del saludo');
        changeAnimation('idle');
      }, 6000);

      // Permitir nuevo saludo después de 10 segundos
      setTimeout(() => {
        setHasGreeted(false);
      }, 10000);
    }
  }, [shouldGreet, hasGreeted, animations]);

  // Manejar baile
  useEffect(() => {
    if (isDancing && animations.dancing) {
      console.log('💃 Iniciando animación de baile...');
      changeAnimation('dancing');
    } else if (!isDancing && currentAnimation === 'dancing') {
      console.log('🛑 Parando animación de baile...');
      changeAnimation('idle');
    }
  }, [isDancing, animations, currentAnimation]);

  // Sistema de variaciones automáticas de idle (basado en SkyBotNMP)
  useEffect(() => {
    if (currentAnimation === 'idle' && !isAnimating && (animations.idle2 || animations.idle3 || animations.idle4 || animations.idle5)) {
      // Tiempo aleatorio entre 5-20 segundos
      const randomDelay = Math.random() * 15000 + 5000;
      console.log(`🎯 Programando variación idle en ${Math.round(randomDelay / 1000)} segundos...`);

      const timer = setTimeout(() => {
        // Array de animaciones de variación disponibles
        const availableVariations = ['idle2', 'idle3', 'idle4', 'idle5'].filter(name => animations[name]);
        
        if (availableVariations.length > 0 && currentAnimation === 'idle') {
          const selectedAnimation = availableVariations[Math.floor(Math.random() * availableVariations.length)];
          console.log(`🎭 Ejecutando variación: ${selectedAnimation}`);
          
          setCurrentAnimation(selectedAnimation);

          if (animations[selectedAnimation] && mixer) {
            mixer.stopAllAction();
            animations[selectedAnimation].reset().play();
          }
          
          // Volver a idle después de que termine la variación
          setTimeout(() => {
            console.log('🔄 Volviendo a idle');
            setCurrentAnimation('idle');
            if (animations.idle && mixer) {
              mixer.stopAllAction();
              animations.idle.reset().play();
            }
          }, animations[selectedAnimation].getClip().duration * 1000);
        }
      }, randomDelay);

      return () => clearTimeout(timer);
    }
  }, [currentAnimation, isAnimating, animations.idle2, animations.idle3, animations.idle4, animations.idle5, animations.idle, mixer]);

  // Animación frame por frame
  useFrame((state, delta) => {
    if (!meshRef.current || !mixer) return;

    // Actualizar mixer de animaciones
    mixer.update(delta);

    // Manejar parpadeo automático
    const currentTime = Date.now();
    handleBlinking(currentTime);
    applyBlinking();

    // Cambiar automáticamente entre animaciones talking cuando esté hablando
    if (emotion === 'speaking' && currentAnimation.startsWith('talking')) {
      const action = animations[currentAnimation];
      if (action && action.time >= action.getClip().duration - 0.1) {
        const availableTalking = ['talking0', 'talking1', 'talking2', 'talking3'].filter(
          name => animations[name] && name !== currentAnimation
        );
        if (availableTalking.length > 0) {
          const newTalking = availableTalking[Math.floor(Math.random() * availableTalking.length)];
          changeAnimation(newTalking);
        }
      }
    }

    // Movimientos sutiles según emoción
    switch (emotion) {
      case 'listening':
        meshRef.current.rotation.y = Math.sin(state.clock.elapsedTime * 1.5) * 0.02;
        break;
      case 'thinking':
        meshRef.current.rotation.x = Math.sin(state.clock.elapsedTime * 0.6) * 0.01;
        break;
      case 'speaking':
        meshRef.current.rotation.y = Math.sin(state.clock.elapsedTime * 2) * 0.01;
        meshRef.current.rotation.x = Math.sin(state.clock.elapsedTime * 1.8) * 0.005;
        break;
      default:
        meshRef.current.rotation.y = Math.sin(state.clock.elapsedTime * 0.5) * 0.01;
    }
  });

  // Mostrar loading mientras carga
  if (isLoading || !model) {
    return (
      <group position={[0, -1.5, 0]}>
        <mesh>
          <boxGeometry args={[0.5, 0.5, 0.5]} />
          <meshStandardMaterial
            color="#DC143C"
            emissive="#DC143C"
            emissiveIntensity={Math.sin(Date.now() * 0.005) * 0.3 + 0.3}
          />
        </mesh>
      </group>
    );
  }

  return (
    <group ref={meshRef} position={[0, -1.5, 0]}>
      <primitive object={model} scale={1.0} />
    </group>
  );
}

const Avatar3D = forwardRef<Avatar3DRef, Avatar3DProps>(({ 
  modelPath = '/models/Greta.glb', 
  emotion = 'idle',
  isAnimating = false,
  shouldGreet = false,
  isDancing = false,
  audioElement = null
}, ref) => {
  const { analyzeAudio, stopLipSync, resetMouth } = useLipSync();
  const [model, setModel] = useState<THREE.Group | null>(null);

  useImperativeHandle(ref, () => ({
    startLipSync: (audio: HTMLAudioElement) => {
      if (model) {
        analyzeAudio(audio, model);
      }
    },
    stopLipSync: () => {
      if (model) {
        stopLipSync(model);
      }
    },
    resetMouth: () => {
      if (model) {
        resetMouth(model);
      }
    }
  }), [model, analyzeAudio, stopLipSync, resetMouth]);
  const getLightColor = () => {
    switch (emotion) {
      case 'listening': return '#4fc3f7';
      case 'thinking': return '#ffa726';
      case 'speaking': return '#66bb6a';
      case 'dancing': return '#e91e63';
      default: return '#ffffff';
    }
  };

  return (
    <CanvasContainer>
      <Canvas camera={{ position: [0, 0, 1.2], fov: 60 }}>
        {/* Luz ambiental más brillante */}
        <ambientLight intensity={0.8} />
        
        {/* Luz principal frontal más intensa */}
        <directionalLight 
          position={[2, 3, 2]} 
          intensity={2.0} 
          castShadow
        />
        
        {/* Luz de relleno lateral más brillante */}
        <directionalLight 
          position={[-2, 2, 1]} 
          intensity={1.2} 
          color="#ffffff"
        />
        
        {/* Luz posterior para contorno */}
        <directionalLight 
          position={[0, 1, -2]} 
          intensity={0.8} 
          color="#f8f8f8"
        />
        
        {/* Luz adicional desde abajo para eliminar sombras */}
        <directionalLight 
          position={[0, -1, 1]} 
          intensity={0.6} 
          color="#ffffff"
        />
        
        {/* Luz dinámica con color según emoción */}
        <pointLight 
          position={[1, 1, 1]} 
          intensity={0.5 + (isAnimating ? 0.3 : 0)} 
          color={getLightColor()}
        />
        
        <Suspense fallback={
          <LoadingText>Cargando Greta...</LoadingText>
        }>
          <AvatarModel 
            modelPath={modelPath} 
            emotion={emotion}
            isAnimating={isAnimating}
            shouldGreet={shouldGreet}
            isDancing={isDancing}
            audioElement={audioElement}
            onModelLoaded={setModel}
          />
        </Suspense>
        
        <OrbitControls 
          enableZoom={false}
          enablePan={false}
          enableRotate={false}
          maxDistance={2.5}
          minDistance={1.2}
        />
      </Canvas>
    </CanvasContainer>
  );
});

Avatar3D.displayName = 'Avatar3D';

export default Avatar3D;
